import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useHistory } from 'react-router-dom';
import type { AwardPaymentRequest } from '../../types/awardPayment';
import { getStatusColorClass } from '../../utils/awardStatusColor';
import PATH from '../../constants/paths';
import InputField from '../Shared/Tailwind/InputField';
import { ACTIVE_REVIEW_STATUSES, normalizePaymentRequestStatus, toDisplayStatus } from './paymentRequestStatuses';
import Table from '../Shared/Tailwind/Table';
import { getAmountRequestedDisplay, getDaysOpen, getRequesterName, parseDateTime } from './awardUtils';

const DAYS_OPEN_ALERT_THRESHOLD = 15;

type Props = {
  allRequests: AwardPaymentRequest[];
  selectedStatus?: string;
  onClearSelectedStatus?: () => void;
  setIsLoading?: (isLoading: boolean) => void;
};

type SortField = 'id' | 'name' | 'type' | 'submitted' | 'daysOpen' | 'amount' | 'status';
type SortDirection = 'asc' | 'desc';

const styles = {
  sectionCard: 'w-full p-4 sm:p-6 md:p-8 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-brand-cyan flex flex-col items-start',
  controlRow: 'w-full pb-2 flex flex-col gap-3 md:flex-row md:items-center md:justify-between text-bold',
  searchInput:
    'w-full h-11 px-3 py-2.5 rounded-sm outline outline-1 outline-offset-[-1px] outline-brand-border text-gray-900 text-base font-normal leading-6',
  tableWrapper: 'w-full overflow-x-auto  min-w-full divide-y divide-gray-300',
  table: 'w-full border-collapse',
  tableHeaderButton: 'inline-flex items-center gap-2 text-left text-brand-navy text-sm font-semibold font-sans leading-5 hover:text-blue',
  tableCell: 'px-4 py-3 border-b border-brand-border text-gray-800 text-sm font-normal font-sans leading-5 align-top',
  daysOpenAlert: 'text-red-600 font-semibold',
  viewButton:
    'w-full xl:w-auto px-6 py-3 rounded-[999px] outline outline-2 outline-offset-[-2px] outline-purple inline-flex justify-center items-center gap-2',
  viewText: 'text-center text-purple text-sm font-bold font-sans uppercase leading-4 tracking-wide',
  emptyStateMessage: 'w-full text-center text-brand-navy text-md font-normal font-sans leading-6',
  tableHeader: 'bg-gray-200 border outline-1 outline-offset-[-1px] outline-brand-border',
  fieldLabel: 'text-left text-blue-950 text-sm font-semibold leading-5',
};

const AwardPaymentReviewerTable: React.FC<Props> = ({ allRequests, selectedStatus, onClearSelectedStatus, setIsLoading }) => {
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  const [sortField, setSortField] = useState<SortField>('submitted');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const reviewerRequests = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    let filteredRequests = allRequests;

    if (selectedStatus) {
      filteredRequests = filteredRequests.filter((request) => normalizePaymentRequestStatus(request.status) === selectedStatus);
    }
    const rows = filteredRequests.map((request) => {
      const normalizedStatus = normalizePaymentRequestStatus(request.status);
      const requesterName = getRequesterName(request);
      const submittedTimestamp = parseDateTime(request.submittedAt)?.toMillis() ?? 0;
      return {
        request,
        normalizedStatus,
        requesterName,
        submittedTimestamp,
        daysOpen: submittedTimestamp === 0 ? 0 : getDaysOpen({ awardPaymentRequest: request, submittedTimestamp }),
      };
    });

    const filteredByStatus = showAllStatuses ? rows : rows.filter((row) => ACTIVE_REVIEW_STATUSES.has(row.normalizedStatus));
    const filteredBySearch = normalizedSearch
      ? filteredByStatus.filter((row) => {
          const amountSearchValue = String(row.request.amountRequestedValue ?? '');
          const amountTextSearchValue = String(row.request.amountRequested ?? '').toLowerCase();
          return (
            row.request.id.toString().includes(normalizedSearch) ||
            (row.request.requestNumber ?? '').toLowerCase().includes(normalizedSearch) ||
            row.requesterName.toLowerCase().includes(normalizedSearch) ||
            row.request.type.toLowerCase().includes(normalizedSearch) ||
            amountTextSearchValue.includes(normalizedSearch) ||
            amountSearchValue.includes(normalizedSearch) ||
            row.normalizedStatus.includes(normalizedSearch)
          );
        })
      : filteredByStatus;

    return filteredBySearch.sort((a, b) => {
      let left: number | string = '';
      let right: number | string = '';

      switch (sortField) {
        case 'id':
          left = a.request.requestNumber ?? a.request.id.toString();
          right = b.request.requestNumber ?? b.request.id.toString();
          break;
        case 'name':
          left = a.requesterName.toLowerCase();
          right = b.requesterName.toLowerCase();
          break;
        case 'type':
          left = a.request.type.toLowerCase();
          right = b.request.type.toLowerCase();
          break;
        case 'submitted':
          left = a.submittedTimestamp;
          right = b.submittedTimestamp;
          break;
        case 'daysOpen':
          left = a.daysOpen;
          right = b.daysOpen;
          break;
        case 'amount':
          left = a.request.amountRequestedValue ?? 0;
          right = b.request.amountRequestedValue ?? 0;
          break;
        case 'status':
          left = a.normalizedStatus;
          right = b.normalizedStatus;
          break;
      }

      if (left < right) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (left > right) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [allRequests, searchTerm, showAllStatuses, sortDirection, sortField, selectedStatus]);

  // When parent selects a status (via the tiles), clear the local "show all" checkbox
  useEffect(() => {
    if (selectedStatus) {
      setShowAllStatuses(false);
    }
  }, [selectedStatus]);

  const handleViewRequest = (id: number, userId?: number) => {
    setIsLoading?.(true);
    const query = userId && userId > 0 ? `?id=${id}&userId=${userId}` : `?id=${id}`;
    history.push(`${PATH.AWARD_PAYMENT_REQUEST}${query}`);
  };

  return (
    <div className={cn(styles.sectionCard)}>
      <div className={cn(styles.controlRow)}>
        <InputField
          name='reviewer-search'
          label='Search requests'
          type='text'
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder='Search by Request #, Name, Type, Amount, or Status'
          wrapperClassName='w-full md:max-w-md'
          inputClassName={styles.searchInput}
          labelClassName={styles.fieldLabel}
        />

        <InputField
          name='reviewer-show-all'
          label='Show all statuses'
          type='checkbox'
          value={showAllStatuses}
          onChange={(val: boolean) => {
            setShowAllStatuses(val);
            // If user turns on "show all" while a status filter is set, clear that filter in parent
            if (val && selectedStatus) {
              onClearSelectedStatus?.();
            }
          }}
          labelClassName={styles.fieldLabel}
        />
      </div>

      <div className={cn(styles.tableWrapper)}>
        <Table
          columns={[
            { id: 'id', label: 'Request #', render: (row: any) => row.request.requestNumber || row.request.id, cellClassName: 'text-left pl-8' },
            { id: 'name', label: 'Name', render: (row: any) => row.requesterName || '-', cellClassName: 'text-left' },
            {
              id: 'type',
              label: 'Type',
              render: (row: any) => (row.request.type ? row.request.type.charAt(0).toUpperCase() + row.request.type.slice(1) : '-'),
            },
            { id: 'submitted', label: 'Submitted', render: (row: any) => row.request.submittedAt || 'N/A' },
            {
              id: 'daysOpen',
              label: 'Days Open',
              render: (row: any) => <span className={cn(row.daysOpen >= DAYS_OPEN_ALERT_THRESHOLD && styles.daysOpenAlert)}>{row.daysOpen}</span>,
            },
            { id: 'amount', label: 'Amount', render: (row: any) => getAmountRequestedDisplay(row.request), cellClassName: 'text-left' },
            {
              id: 'status',
              label: 'Status',
              render: (row: any) => (
                <span className={cn(getStatusColorClass(normalizePaymentRequestStatus(row.request.status)))}>
                  {toDisplayStatus(row.request.status)}
                </span>
              ),
            },
          ]}
          rows={reviewerRequests.map((r) => ({ ...r, id: r.request.id }))}
          keyField='id'
          onRowClick={(row: any) => handleViewRequest(row.request.id, row.request.userId)}
          onSort={(field: string) => handleSort(field as SortField)}
          sortField={sortField}
          sortDirection={sortDirection}
          emptyState={<p className={cn(styles.emptyStateMessage)}>There are no award payment requests.</p>}
          tableClassName={styles.table}
          headerClassName={styles.tableHeader}
          cellClassName={styles.tableCell}
          pagination={{ enabled: true, pageSize: 10 }}
        />
      </div>
    </div>
  );
};

export default AwardPaymentReviewerTable;
